import {
    PortableText,
    PortableTextReactComponents,
    toPlainText,
} from "@portabletext/react";
import { NextSeo } from "next-seo";
import Link from "next/link";
import Header from "../../components/Header/Header";
import Hero from "./components/Hero/Hero";
import { urlFor } from "../../../lib/sanity";
import PostImage from "../../components/Sanity/Image/Image";
import Newsletter from "../../components/Footer/components/Newsletter/Newsletter";
import Footer from "../../components/Footer/Footer";
import dynamic from "next/dynamic";
import { formatDate, trimText } from "../../../common/helper";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { dracula } from "react-syntax-highlighter/dist/cjs/styles/prism";
import { Children } from "react";
import { Meetups } from "../../../common/types";

const SocialShare = dynamic(
    () => import("../../components/SocialShare/SocialShare"),
    { ssr: false }
);

const components: Partial<PortableTextReactComponents> = {
    types: {
        image: PostImage,
    },
    marks: {
        code: ({ children }) => (
            <code className="bg-gray-light text-gray-dark p-1 rounded">
                {children}
            </code>
        ),
    },
    block: {
        pre: ({ children }) => {
            const code = Children.map(children, function (child) {
                if (typeof child == "string") return child;
            }).join("\n");

            return (
                <SyntaxHighlighter language="javascript" style={dracula}>
                    {code}
                </SyntaxHighlighter>
            );
        },
    },
};

interface MeetupProps {
    preview: any;
    post: Meetups;
}

export default function Meetup({ preview, post }: MeetupProps) {
    const { title, date, mainImage, body, tags, city, link } = post;

    return (
        <div>
            {preview && (
                <Link href="/api/exit-preview">
                    <a className="block fixed z-40 bottom-0 left-0 ml-8 mb-8">
                        <span className="btn-primary bg-red">Preview Mode</span>
                    </a>
                </Link>
            )}
            <Header />
            <NextSeo
                title={`${title}`}
                description={
                    body ? trimText(toPlainText(body as any), 200) + "..." : ""
                }
                canonical={`https://www.dzobs.com/blog/${post?.slug}`}
                openGraph={{
                    images: [
                        {
                            url:
                                mainImage && mainImage.hasOwnProperty("asset")
                                    ? urlFor(mainImage).url()
                                    : "https://www.dzobs.com/social.png",
                        },
                    ],
                }}
            />
            <Hero
                title={title}
                subheading={date ? formatDate(date) : ""}
                tags={tags}
                city={city}
            />
            <article className="blog-post max-w-screen-md mx-auto px-6 lg:px-0 mb-8 mt-8 leading-loose">
                {mainImage && mainImage.hasOwnProperty("asset") && (
                    <figure className="mb-4">
                        <img
                            src={urlFor(mainImage).width(1200).url()}
                            className="w-full"
                        />
                        <figcaption className="text-sm text-gray-500 mt-1">
                            {mainImage.caption}
                        </figcaption>
                    </figure>
                )}

                <div role="text">
                    <PortableText value={body as any} components={components} />
                </div>
                <div className="text-center p-4 sticky md:static md:my-16 bottom-0 flex align-center justify-center width-100 shadow md:shadow-none bg-white">
                    <a href={link} target="_blank" className="btn-primary">
                        Pogledaj više
                    </a>
                </div>
            </article>

            <Newsletter bg="bg-gray-light" />
            <Footer />
            <SocialShare title={post.title} term="tekst" />
        </div>
    );
}
